// src/components/OptionFields/RangeFieldRenderer.tsx
// 範囲検索用のフィールドレンダラー

import React from 'react';
import { Input, DatePicker, Space, Typography, Button } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

interface RangeFieldRendererProps {
  fieldType: number; // 1=数値範囲, 6=日付範囲
  format?: string; // 日付フォーマット
  fromValue?: any;
  toValue?: any;
  displayValue?: string;
  onFromChange: (value: any) => void;
  onToChange: (value: any) => void;
  onDisplayChange: (value: string) => void;
  placeholder: string;
  className?: string;
}

/**
 * 範囲検索用のフィールドレンダラー
 * From/To形式での範囲指定を可能にする
 */
export const RangeFieldRenderer: React.FC<RangeFieldRendererProps> = ({
  fieldType,
  format,
  fromValue,
  toValue,
  displayValue,
  onFromChange,
  onToChange,
  onDisplayChange,
  placeholder,
  className
}) => {
  if (fieldType === 1) {
    // 数値範囲
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input
          value={displayValue || ''}
          onChange={(e) => onDisplayChange(e.target.value)}
          placeholder={placeholder}
          className={className}
          readOnly
        />
        <Space.Compact style={{ width: '100%' }}>
          <Input
            value={fromValue || ''}
            onChange={(e) => onFromChange(e.target.value)}
            placeholder="最小値"
            style={{ width: '50%' }}
            addonBefore="≥"
          />
          <Input
            value={toValue || ''}
            onChange={(e) => onToChange(e.target.value)}
            placeholder="最大値"
            style={{ width: '50%' }}
            addonBefore="≤"
          />
        </Space.Compact>
      </Space>
    );
  } else if (fieldType === 6) {
    // 日付範囲
    const dateFormat = format || 'YYYY/MM/DD';
    const picker = dateFormat.includes('MM') && !dateFormat.includes('DD') ? 'month' : 'date';
    // プレースホルダーを picker に応じて変更
    const placeholderFrom = picker === 'month' ? '開始月' : '開始日';
    const placeholderTo = picker === 'month' ? '終了月' : '終了日';

    return (
      <Space direction="vertical" style={{ width: '100%' }} size="small">
      {/* 開始日/開始月 */}
      <Space.Compact style={{ width: '100%' }}>
        <DatePicker
          value={fromValue ? dayjs(fromValue) : null}
          onChange={(date) => onFromChange(date ? date.format(dateFormat) : null)}
          format={dateFormat}
          picker={picker}
          placeholder={placeholderFrom}
          style={{ flexGrow: 1 }}
          showToday={false}
          getPopupContainer={() => document.body}
          placement="bottomLeft"
        />
        <Button
          onClick={() => onFromChange(null)}
          icon={<CloseCircleFilled style={{ color: '#bfbfbf' }} />}
          style={{ flexShrink: 0, padding: '0 4px', border: '1px solid #e0e0e0', boxShadow: 'none', background: 'transparent' }}
          title="クリア"
        />
      </Space.Compact>

      {/* 終了日/終了月 */}
      <Space.Compact style={{ width: '100%' }}>
        <DatePicker
          value={toValue ? dayjs(toValue) : null}
          onChange={(date) => onToChange(date ? date.format(dateFormat) : null)}
          format={dateFormat}
          picker={picker}
          placeholder={placeholderTo}
          style={{ flexGrow: 1 }}
          showToday={false}
          getPopupContainer={() => document.body}
          placement="bottomLeft"
        />
        <Button
          onClick={() => onToChange(null)}
          icon={<CloseCircleFilled style={{ color: '#bfbfbf' }} />}
          style={{ flexShrink: 0, padding: '0 4px', border: '1px solid #e0e0e0', boxShadow: 'none', background: 'transparent' }}
          title="クリア"
        />
      </Space.Compact>
    </Space>
    );
  }

  return null;
};

export default RangeFieldRenderer;